import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */

// 金の短罫 + 見出しテキストの小見出し（ArticleSectionBand の帯は全廃）
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

// 出典元サイトの実スクショ引用（figure + 出典URLキャプション）
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}

// 本文流れの中の大きめ数字強調（独立カードにしない）
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function DowntonAbbeyGrandFinalePage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: 'Downton Abbey: The Grand Finale 映画公開' }
  ];

  const article = {
    category: 'イベント',
    date: '2025年9月12日',
    title: 'Downton Abbey: The Grand Finale 映画公開',
    subtitle: '執事文化の集大成として世界的注目',
    heroImage: '/images/generated/jba-fs-068-news_downton-abbey-grand-finale.webp',
    lead: '世界的大ヒットシリーズの最終映画が公開され、興行収入4,600万ドルを記録。伝説的バトラーMr. Carsonが復帰し、執事という職業の伝統と誇りを描いた感動的な作品として、執事文化の集大成となっています。',
    comment: '「Downton Abbey: The Grand Finale」の世界的成功は、執事という職業の普遍的価値と魅力を改めて証明するものとして、弊協会としても大変喜ばしく思っております。Mr. Carsonというキャラクターを通じて描かれる執事の専門性、忠誠心、そして何よりも「おもてなしの心」は、まさに私どもが目指す執事サービスの理想像そのものです。\n\nこの映画が世界中で愛され続けることで、執事という職業への理解と尊敬が深まり、日本における執事文化の発展にも大きな影響を与えることを期待しております。私どもとしても、この作品が示す執事の真の価値を日本の皆様にお伝えし、より質の高い執事サービスの提供に努めてまいります。',
    related: [
      { to: '/news/apple-robot-butler', image: '/images/generated/jba-fs-072-news_downton-abbey-grand-finale-v3.webp', aspect: 'aspect-[4/3]', alt: 'Apple ロボットバトラー開発プロジェクトの関連記事', title: 'Apple ロボットバトラー開発プロジェクト', desc: 'AI技術が切り開く執事サービスの未来' },
      { to: '/news/raffles-london-campaign', image: '/images/generated/jba-fs-073-news_downton-abbey-grand-finale-v3.webp', aspect: 'aspect-[3/4]', alt: 'Raffles London「The Butler Did It」キャンペーン第2章の関連記事', title: 'Raffles London「The Butler Did It」キャンペーン第2章', desc: 'ラグジュアリーホテルの執事サービス革新' }
    ]
  };

  // 記事末尾の出典一覧（実際に閲覧・撮影した実在ソースのURL付き）
  const sources = [
    { label: '作品概要・製作', value: 'https://en.wikipedia.org/wiki/Downton_Abbey:_The_Grand_Finale', link: true },
    { label: '興行収入データ', value: 'https://www.boxofficemojo.com/release/rl1014267905/', link: true },
    { label: '批評家・観客評価', value: 'https://www.rottentomatoes.com/m/downton_abbey_the_grand_finale', link: true },
    { label: '公式サイト', value: 'https://www.focusfeatures.com/downton-abbey-the-grand-finale', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];

  const toc = [
    { id: 's-success', label: '映画の成功と興行収入' },
    { id: 's-production', label: '作品データと製作背景' },
    { id: 's-boxoffice', label: '興行収入データ' },
    { id: 's-reception', label: '批評家・観客の評価' },
    { id: 's-impact', label: '執事文化への影響と意義' },
    { id: 's-history', label: 'シリーズの歩み' },
    { id: 's-global', label: '国際的な反響と文化的価値' }
  ];

  return (
    <Layout>
      <ArticleHero
        eyebrow="News / Event"
        category={article.category}
        title={article.title}
        subtitle={article.subtitle}
        date={article.date}
        image={article.heroImage}
      />
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②） */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* リード（全幅はここだけ許容） */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      {/* 本文（単一エディトリアルカラム）＋ サイドバー（lg以上・sticky） */}
      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          {/* エディトリアルカラム */}
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            {/* この記事のポイント */}
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                'シリーズ三部作の完結編が2025年9月12日に全米公開され、世界興行収入は約1億354万ドルを記録した',
                '伝説的バトラーMr. Carson（ジム・カーター演）が復帰し、執事という職業の伝統と誇りが物語の核に置かれた',
                'ロッテン・トマトの批評家スコア92%（Certified Fresh）と、テレビから15年続いた物語の大団円が高く評価された'
              ]}
            />

            {/* 01: 映画の成功と興行収入（既存本文・一字も削らず） */}
            <SectionHeading id="s-success" en="Box Office">映画の成功と興行収入</SectionHeading>
            <div className="space-y-6">
              <p className={P}>「Downton Abbey: The Grand Finale」は公開初週末で4,600万ドルの興行収入を記録し、世界的な成功を収めました。この映画は、2019年の第1作映画に続く待望の続編として、ファンの期待を大きく上回る結果となりました。</p>
              <p className={P}>特に注目すべきは、伝説的バトラーであるMr. Carson（ジム・カーター演）の復帰です。彼の存在は、執事という職業の伝統と誇りを象徴するものとして、映画全体に深い意味を与えています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-focusfeatures.jpg"
              alt="配給元フォーカス・フィーチャーズの公式サイトに掲載された映画Downton Abbey: The Grand Finaleの作品紹介ページのスクリーンショット"
              media="Focus Features 公式サイト"
              url="https://www.focusfeatures.com/downton-abbey-the-grand-finale"
            />

            {/* 引用（帯ではなく本文中の象徴句・既存） */}
            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="伝説的バトラーMr. Carsonが復帰し、執事という職業の伝統と誇りを描いた感動的な作品。" cite="Downton Abbey: The Grand Finale" />
            </div>

            {/* 02(増補): 作品データと製作背景 */}
            <SectionHeading id="s-production" en="Production">作品データと製作背景</SectionHeading>
            <div className="space-y-6">
              <p className={P}>本作『Downton Abbey: The Grand Finale』は、シリーズを一貫して手がけてきた脚本家ジュリアン・フェロウズ（Julian Fellowes）が脚本を、サイモン・カーティス（Simon Curtis）が監督を務めた2025年の歴史ドラマ映画です。</p>
              <p className={P}>2022年公開の前作『新たなる時代（A New Era）』に続く三部作の完結編であり、純製作費は約<Stat>5,000万ドル</Stat>。上映時間は2時間3分、レーティングはPGで、配給は2019年の第1作から一貫してフォーカス・フィーチャーズ（Focus Features）が担いました。</p>
              <p className={P}>キャストにはヒュー・ボネヴィル、ミシェル・ドッカリー、エリザベス・マクガヴァン、ポール・ジアマッティら、テレビシリーズと過去2作の映画に出演した主要俳優が再結集。執事のカーソン氏を演じるジム・カーターも変わらぬ存在感で本作に帰還しました。</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-wikipedia.jpg"
              alt="Wikipediaの英語版に掲載された映画Downton Abbey: The Grand Finaleの監督・脚本・製作費など作品概要のスクリーンショット"
              media="Wikipedia（英語版）"
              url="https://en.wikipedia.org/wiki/Downton_Abbey:_The_Grand_Finale"
            />

            {/* 03(増補): 興行収入データ（数字は本文流れの中で強調） */}
            <SectionHeading id="s-boxoffice" en="Box Office Data">興行収入データ</SectionHeading>
            <div className="space-y-6">
              <p className={P}>公開後、本作は世界興行収入 <Stat>1億354万ドル</Stat> を売り上げ、シリーズの掉尾を飾る商業的成功を収めました。内訳は北米（米国・カナダ）が約<Stat>4,499万ドル</Stat>、北米以外の国際市場が約<Stat>5,854万ドル</Stat>で、国際市場が全体の約56.5%を占め、シリーズの世界的な支持の広がりを裏づけています。</p>
              <p className={P}>全米公開初週末（オープニング）の興行収入は約1,810万ドルで、3,694館で封切られ、最大公開規模は3,711館に達しました。配給はフォーカス・フィーチャーズ、全米公開日は2025年9月12日です。</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-boxofficemojo.jpg"
              alt="Box Office Mojoに掲載された本作の北米・国際・世界の各興行収入と公開館数データ画面のスクリーンショット"
              media="Box Office Mojo"
              url="https://www.boxofficemojo.com/release/rl1014267905/"
            />

            {/* 04(増補): 批評家・観客の評価 */}
            <SectionHeading id="s-reception" en="Reception">批評家・観客の評価</SectionHeading>
            <div className="space-y-6">
              <p className={P}>批評面でも本作は高い評価を獲得しました。レビュー集計サイトのロッテン・トマト（Rotten Tomatoes）では、批評家スコア <Stat>92%</Stat>（142件のレビュー）で「Certified Fresh（認定フレッシュ）」を獲得。観客による評価（Popcornmeter）はさらに高い <Stat>95%</Stat> に達し、長年シリーズを愛してきたファンからも強い支持を集めました。</p>
              <p className={P}>三部作の最終章として、物語のけじめと登場人物たちへの惜別を丁寧に描いた点が、批評家・観客の双方から支持された要因とされています。</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-rottentomatoes.jpg"
              alt="Rotten Tomatoesに掲載された本作の批評家スコア92パーセントと観客スコア95パーセントの評価画面のスクリーンショット"
              media="Rotten Tomatoes"
              url="https://www.rottentomatoes.com/m/downton_abbey_the_grand_finale"
            />

            {/* 05: 執事文化への影響と意義（既存の要点ボックス＋本文・一字も削らず） */}
            <SectionHeading id="s-impact" en="Cultural Impact">執事文化への影響と意義</SectionHeading>
            <div className="my-6">
              <KeyPointsBox
                title="この映画がもたらしたもの"
                points={[
                  '執事という職業を現代に蘇らせ、その専門性と重要性を世界中に知らしめた',
                  '執事は単なる使用人ではなく、家庭の運営に不可欠な専門職として描かれた',
                  '執事養成学校への入学希望者が世界各地で増加するなど、具体的な影響を与えた'
                ]}
              />
            </div>
            <div className="space-y-6">
              <p className={P}>Downton Abbeyシリーズは、執事という職業を現代に蘇らせ、その専門性と重要性を世界中に知らしめた画期的な作品です。Mr. Carsonをはじめとする執事キャラクターたちは、単なる使用人ではなく、家庭の運営において不可欠な専門職として描かれています。</p>
              <p className={P}>この映画の成功は、現代社会における執事サービスへの関心の高まりを反映しており、実際に世界各地で執事養成学校への入学希望者が増加するなど、具体的な影響を与えています。</p>
            </div>

            {/* 06(増補): シリーズの歩み */}
            <SectionHeading id="s-history" en="The Journey">シリーズの歩み</SectionHeading>
            <div className="space-y-6">
              <p className={P}>『ダウントン・アビー』は、2010年に英ITVで放送が始まったテレビドラマとして世界的な人気を博し、2015年まで全6シーズンにわたって放送されました。その後、2019年に第1作の劇場版が公開され、2022年には続編『新たなる時代（A New Era）』が公開。そして2025年の本作『The Grand Finale』が、テレビから数えて<Stat>15年</Stat>にわたる物語の完結編となります。</p>
              <p className={P}>カーソン氏（Mr. Carson）をはじめとする使用人たちと、貴族クローリー家の人々が織りなす階上と階下の物語は、本作をもって大団円を迎えました。時代の変化のなかで受け継がれてきた「仕える誇り」の系譜が、ひとつの区切りに至ったのです。</p>
            </div>
            <SourceShot
              src="/images/news-sources/downton-thenumbers.jpg"
              alt="The Numbersに掲載された本作の公開データと興行成績を一覧するページのスクリーンショット"
              media="The Numbers"
              url="https://www.the-numbers.com/movie/Downton-Abbey-The-Grand-Finale-(2025-United-Kingdom)"
            />

            {/* 07: 国際的な反響と文化的価値（既存本文・一字も削らず） */}
            <SectionHeading id="s-global" en="Global Response">国際的な反響と文化的価値</SectionHeading>
            <div className="space-y-6">
              <p className={P}>この映画は英国だけでなく、アメリカ、カナダ、オーストラリア、日本など世界各国で同時公開され、どの国でも高い評価を受けています。特に、執事という職業に対する理解と尊敬の念が国際的に広がったことは、文化的に非常に意義深いものです。</p>
              <p className={P}>批評家からは「執事という職業の尊厳と専門性を見事に描いた傑作」「現代社会が失いつつあるサービスの真髄を思い出させる作品」として高く評価されており、単なるエンターテインメントを超えた文化的価値を持つ作品として位置づけられています。</p>
            </div>
          </article>

          {/* サイドバー（lg以上・sticky）。モバイルでは非表示にし、末尾の出典/関連が本文後方に来る */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">公開日</dt><dd className="text-[#333] mt-0.5">2025年9月12日（全米）</dd></div>
                  <div><dt className="text-[#8f7a56]">配給</dt><dd className="text-[#333] mt-0.5">Focus Features</dd></div>
                  <div><dt className="text-[#8f7a56]">カテゴリ</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                  <div><dt className="text-[#8f7a56]">世界興行収入</dt><dd className="text-[#333] mt-0.5">約1億354万ドル</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* 協会コメント */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      {/* 出典一覧 */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      {/* 関連記事 */}
      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
